/***
*
*   WORKSPACE DETAIL
*   View detailed information about a single workspace
*
**********/

import { useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Animate,
  Card,
  Button,
  Loader,
  Grid,
  Stat,
  Table,
  Breadcrumbs,
  ViewContext,
  Paginate,
  Icon
} from 'components/lib';
import {
  useWorkspaceDetail,
  useWorkspaceUsers,
  useWorkspaceEmployees,
  useWorkspaceBilling,
  useWorkspaceFeatures,
  useWorkspaceMutations
} from 'components/hooks/useWorkspaceManagement';

const Style = {
  detailHeader: 'flex justify-between items-start mb-6 bg-white rounded-lg border border-slate-200 p-6',
  detailHeaderInfo: 'flex-1',
  detailTitle: 'text-2xl font-bold text-slate-800',
  detailMeta: 'flex gap-2 mt-2 flex-wrap',
  detailActions: 'flex gap-3',
  statusBadge: 'px-3 py-1 rounded-full text-xs font-medium',
  statusActive: 'bg-green-100 text-green-700',
  statusInactive: 'bg-red-100 text-red-700',
  planBadge: 'px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700',
  trialBadge: 'px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700',
  tabsContainer: 'flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto',
  tab: 'flex items-center gap-2 px-4 py-3 text-sm font-medium text-slate-500 cursor-pointer hover:text-slate-700 border-b-2 border-transparent transition-colors whitespace-nowrap',
  tabActive: 'flex items-center gap-2 px-4 py-3 text-sm font-medium text-brand-500 cursor-pointer border-b-2 border-brand-500 whitespace-nowrap',
  tabCount: 'ml-1 px-2 py-0.5 text-xs bg-slate-100 rounded-full',
  tabContent: 'space-y-6',
  infoGrid: 'space-y-3',
  infoRow: 'flex justify-between py-2 border-b border-slate-100 last:border-0',
  infoLabel: 'text-sm text-slate-500',
  infoValue: 'text-sm font-medium text-slate-800',
  featuresGrid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
  featureCard: 'bg-slate-50 rounded-lg p-4',
  featureHeader: 'flex justify-between items-center mb-2',
  featureName: 'text-sm font-medium text-slate-700',
  featureLimit: 'text-xs text-slate-500',
  featureProgress: 'h-2 bg-slate-200 rounded-full overflow-hidden',
  featureProgressBar: 'h-full bg-brand-500 rounded-full transition-all',
  planFeaturesGrid: 'grid grid-cols-1 md:grid-cols-2 gap-3',
  planFeatureItem: 'flex items-center gap-2 p-3 bg-slate-50 rounded-lg',
  planFeatureName: 'text-sm font-medium text-slate-700 flex-1',
  planFeatureLimit: 'text-xs text-slate-500',
};

export function WorkspaceDetail(props) {
  const context = useContext(ViewContext);
  const { id: workspaceId } = useParams();

  const [activeTab, setActiveTab] = useState('overview');
  const [usersPage, setUsersPage] = useState(1);
  const [employeesPage, setEmployeesPage] = useState(1);

  const mutations = useWorkspaceMutations();

  // Fetch data
  const workspace = useWorkspaceDetail(workspaceId);
  const users = useWorkspaceUsers(workspaceId, usersPage, 25);
  const employees = useWorkspaceEmployees(workspaceId, employeesPage, 25);
  const billing = useWorkspaceBilling(workspaceId);
  const features = useWorkspaceFeatures(workspaceId);

  const ws = workspace.data;

  if (workspace.loading) {
    return <Loader />;
  }

  if (!ws) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-500">Workspace not found</p>
        </div>
      </Card>
    );
  }

  const handleToggleStatus = async () => {
    const action = ws.active ? 'deactivate' : 'activate';
    try {
      if (ws.active) {
        await mutations.deactivate(workspaceId);
      } else {
        await mutations.activate(workspaceId);
      }
      context.notification.show(`Workspace ${action}d successfully`, 'success', true);
      window.location.reload();
    } catch (err) {
      context.notification.show(`Failed to ${action} workspace`, 'error', true);
    }
  };

  const handleExport = async () => {
    try {
      await mutations.exportWorkspace(workspaceId);
      context.notification.show('Export started', 'success', true);
    } catch (err) {
      context.notification.show('Failed to export data', 'error', true);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'info' },
    { id: 'users', label: 'Users', icon: 'users', count: users?.data?.total || 0 },
    { id: 'employees', label: 'Employees', icon: 'briefcase', count: employees?.data?.total || 0 },
    { id: 'billing', label: 'Billing', icon: 'credit-card' },
    { id: 'features', label: 'Features', icon: 'sliders' }
  ];

  return (
    <Animate type="pop">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[
        { name: 'Organizations', url: '/workspaces' },
        { name: ws.name, url: `/workspaces/${workspaceId}` }
      ]} />

      {/* Header */}
      <div className={Style.detailHeader}>
        <div className={Style.detailHeaderInfo}>
          <h1 className={Style.detailTitle}>{ws.name}</h1>
          <div className={Style.detailMeta}>
            <span className={`${Style.statusBadge} ${ws.active ? Style.statusActive : Style.statusInactive}`}>
              {ws.active ? 'Active' : 'Inactive'}
            </span>
            <span className={Style.planBadge}>{ws.plan_name || ws.plan || 'Free'}</span>
            {ws.trial_end_date && new Date(ws.trial_end_date) > new Date() && (
              <span className={Style.trialBadge}>
                Trial ends {new Date(ws.trial_end_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className={Style.detailActions}>
          <Button
            text="Export Data"
            icon="download"
            color="blue"
            outline
            action={handleExport}
          />
          <Button
            text={ws.active ? 'Deactivate' : 'Activate'}
            icon={ws.active ? 'x-circle' : 'check-circle'}
            color={ws.active ? 'red' : 'green'}
            action={handleToggleStatus}
            loading={mutations.loading}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className={Style.tabsContainer}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? Style.tabActive : Style.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            <Icon image={tab.icon} size={16} />
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className={Style.tabCount}>{tab.count}</span>
            )}
          </div>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className={Style.tabContent}>
          <Grid cols="4">
            <Stat
              value={ws.stats?.user_count || 0}
              label="Users"
              icon="users"
            />
            <Stat
              value={ws.stats?.employee_count || 0}
              label="Employees"
              icon="briefcase"
            />
            <Stat
              value={ws.stats?.feature_usage?.usage?.file_storage ? `${(ws.stats?.feature_usage?.usage?.file_storage || 0).toFixed(2)} MB` : '0 MB'}
              label="Storage Used"
              icon="hard-drive"
            />
            <Stat
              value={new Date(ws.date_created || ws.createdAt).toLocaleDateString()}
              label="Created"
              icon="calendar"
            />
          </Grid>

          <Grid cols="3">
            <Card title="Workspace Information">
              <div className={Style.infoGrid}>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>ID</span>
                  <span className={Style.infoValue}>{ws.id || ws._id}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Name</span>
                  <span className={Style.infoValue}>{ws.name || 'N/A'}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Slug</span>
                  <span className={Style.infoValue}>{ws.slug || 'N/A'}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Owner Name</span>
                  <span className={Style.infoValue}>{ws.owner_name || 'N/A'}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Owner Email</span>
                  <span className={Style.infoValue}>{ws.owner_email || 'N/A'}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Plan</span>
                  <span className={Style.infoValue}>{ws.plan_name || ws.plan || 'Free'}</span>
                </div>

                {/* Trial plan info */}
                {ws.plan === 'trial' &&
                  <>
                    <div className={Style.infoRow}>
                      <span className={Style.infoLabel}>Trial Start Date</span>
                      <span className={Style.infoValue}>{new Date(ws.trial_start_date).toLocaleDateString()}</span>
                    </div>
                    <div className={Style.infoRow}>
                      <span className={Style.infoLabel}>Trial End Date</span>
                      <span className={Style.infoValue}>{new Date(ws.trial_end_date).toLocaleDateString()}</span>
                    </div>
                  </>
                }

                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Workspace Status</span>
                  <span className={Style.infoValue}>{ws.active ? 'Active' : 'Inactive'}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Created At</span>
                  <span className={Style.infoValue}>{new Date(ws.date_created || ws.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </Card>

            <Card title="Manage People Information">
              <div className={Style.infoGrid}>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>People Limit</span>
                  <span className={Style.infoValue}>{ws?.features?.manage_people?.limit || 0}</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>People Used</span>
                  <span className={Style.infoValue}>{ws.stats?.feature_usage?.usage?.manage_people || 0}</span>
                </div>
              </div>
            </Card>

            <Card title="Storage Information">
              <div className={Style.infoGrid}>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Storage Used</span>
                  <span className={Style.infoValue}>{ws.stats?.feature_usage?.usage?.file_storage || 0} MB</span>
                </div>
                <div className={Style.infoRow}>
                  <span className={Style.infoLabel}>Storage Allocated</span>
                  <span className={Style.infoValue}>{ws?.features?.file_storage?.limit || 0} MB</span>
                </div>
              </div>
            </Card>
          </Grid>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className={Style.tabContent}>
          <Card>
            {users.loading ? (
              <Loader />
            ) : (
              <Table
                data={users.data?.results?.map(u => ({
                  id: u.id || u._id,
                  name: u.first_name + " " + u.last_name || 'N/A',
                  email: u.email,
                  role: u.workspaces?.length ? u.workspaces[0].role_name : 'N/A',
                  account_type: u.workspaces?.length ? u.workspaces[0].account_type.toUpperCase() : 'N/A',
                  verified: u.verified ? 'Yes' : 'No',
                  last_active: u.last_active ? new Date(u.last_active).toLocaleDateString() : 'Never'
                  // 2fa_enabled: u["2fa_enabled"] ? 'Yes' : 'No'
                })) || []}
                show={['name', 'email', 'role', 'account_type', 'verified', 'last_active']}
              />
            )}
          </Card>
          {users.data && users.data.total > 25 && (
            <Paginate
              offset={(usersPage - 1) * 25}
              limit={25}
              total={users.data.total}
              onChange={(offset) => setUsersPage(Math.floor(offset / 25) + 1)}
            />
          )}
        </div>
      )}

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div className={Style.tabContent}>
          <Card>
            {employees.loading ? (
              <Loader />
            ) : (
              <Table
                data={employees.data?.results?.map(e => ({
                  id: e.id || e._id,
                  name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'N/A',
                  email: e.email || 'N/A',
                  department: e.department_option || 'N/A',
                  position: e.job_title_option || 'N/A',
                  status: e.status_option || 'N/A',
                  hire_date: e.joining_date ? new Date(e.joining_date).toLocaleDateString() : 'N/A'
                })) || []}
                show={['name', 'email', 'department', 'position', 'status', 'hire_date']}
                badge={{ col: 'status', color: 'blue' }}
              />
            )}
          </Card>
          {employees.data && employees.data.total > 25 && (
            <Paginate
              offset={(employeesPage - 1) * 25}
              limit={25}
              total={employees.data.total}
              onChange={(offset) => setEmployeesPage(Math.floor(offset / 25) + 1)}
            />
          )}
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className={Style.tabContent}>
          <Grid cols="2">
            <Card title="Subscription">
              {billing.loading ? (
                <Loader />
              ) : billing.data?.subscription ? (
                <div className={Style.infoGrid}>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>Plan</span>
                    <span className={Style.infoValue}>{ws.plan_name || ws.plan}</span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>Status</span>
                    <span className={Style.infoValue}>{billing.data.subscription.status}</span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>Current Period End</span>
                    <span className={Style.infoValue}>
                      {billing.data.subscription.current_period_end
                        ? new Date(billing.data.subscription.current_period_end * 1000).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">No active subscription</p>
              )}
            </Card>

            <Card title="Payment Method">
              {billing.loading ? (
                <Loader />
              ) : billing.data?.payment_method ? (
                <div className={Style.infoGrid}>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>Type</span>
                    <span className={Style.infoValue}>{billing.data.payment_method.type}</span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>Last 4</span>
                    <span className={Style.infoValue}>**** {billing.data.payment_method.last4}</span>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">No payment method on file</p>
              )}
            </Card>
          </Grid>

          <Card title="Recent Invoices">
            {billing.loading ? (
              <Loader />
            ) : billing.data?.invoices?.length > 0 ? (
              <Table
                data={billing.data.invoices.map(inv => ({
                  id: inv.id,
                  date: new Date(inv.created * 1000).toLocaleDateString(),
                  amount: `$${(inv.amount_paid / 100).toFixed(2)}`,
                  status: inv.status,
                  pdf: inv.invoice_pdf
                }))}
                show={['date', 'amount', 'status']}
                badge={{ col: 'status', color: 'green' }}
                actions={{
                  custom: [
                    {
                      icon: 'download',
                      action: (row) => window.open(row.pdf, '_blank'),
                      title: 'Download Invoice'
                    }
                  ]
                }}
              />
            ) : (
              <p className="text-slate-500">No invoices found</p>
            )}
          </Card>
        </div>
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <div className={Style.tabContent}>
          <Card title="Feature Usage">
            {features.loading ? (
              <Loader />
            ) : Object.keys(features.data)?.length > 0 ? (
              <div className={Style.featuresGrid}>
                {features.data?.limitKeyData?.map((feature, index) => (
                  <div key={index} className={Style.featureCard}>
                    <div className={Style.featureHeader}>
                      <span className={Style.featureName}>{feature.feature_key}</span>
                      <span className={Style.featureLimit}>
                        {features.data?.featureUsageData?.usage[feature.feature_key] || 0} / {feature.limit || '∞'}
                      </span>
                    </div>
                    <div className={Style.featureProgress}>
                      <div
                        className={Style.featureProgressBar}
                        style={{
                          width: feature.limit
                            ? `${Math.min((features.data?.featureUsageData?.usage[feature.feature_key] / feature.limit) * 100, 100)}%`
                            : '0%'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No feature usage data available</p>
            )}
          </Card>

          <Card title="Plan Features">
            {ws.features && Object.keys(ws.features).length > 0 ? (
              <div className={Style.planFeaturesGrid}>
                {Object.entries(ws.features).map(([key, value]) => (
                  <div key={key} className={Style.planFeatureItem}>
                    <Icon image={value.enabled ? 'check' : 'x'} color={value.enabled ? 'green' : 'red'} size={16} />
                    <span className={Style.planFeatureName}>{key}</span>
                    {value.limit && (
                      <span className={Style.planFeatureLimit}>Limit: {value.limit}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No plan features configured</p>
            )}
          </Card>
        </div>
      )}
    </Animate>
  );
}